import React, { useState, useEffect } from 'react';
import { Language } from '../translations';

interface AboutProps {
    t: any;
    TRUST_ITEMS: any[];
    lang: Language;
    homeSections?: any[];
}

const DEFAULT_FALLBACK_IMAGES = [
    "https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg",
    "https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/cerrocastillo.jpg"
];

const ImageCarousel: React.FC<{ images: string[] }> = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (images.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % images.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [images]);

    if (!images || images.length === 0) return null;

    return (
        <div className="relative w-full aspect-[16/9] md:h-[550px] overflow-hidden border border-rock rounded-[16px] shadow-2xl group">
            {/* Images Wrapper */}
            <div 
                className="flex h-full transition-transform duration-700 ease-in-out"
                style={{
                    width: `${images.length * 100}%`,
                    transform: `translateX(-${currentIndex * (100 / images.length)}%)`
                }}
            >
                {images.map((img, idx) => (
                    <div 
                        key={idx} 
                        className="h-full flex-shrink-0"
                        style={{ width: `${100 / images.length}%` }}
                    >
                        <img 
                            src={img} 
                            alt={`Fisura Expeditions Carousel ${idx + 1}`}
                            className="w-full h-full object-cover"
                        />
                    </div>
                ))}
            </div>

            {/* Indicator dots */}
            {images.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full z-10">
                    {images.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                idx === currentIndex ? 'bg-[#00d4ff] scale-125' : 'bg-white/30 hover:bg-white/60'
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const About: React.FC<AboutProps> = ({ t, lang, homeSections }) => {

    const getSection = (id: string) => {
        return (homeSections || []).find(s => s.id === id);
    };

    const getSectionContent = (id: string, fallback: any) => {
        const sec = getSection(id);
        if (!sec) return { ...fallback, is_visible: true };
        const langContent = sec[`content_${lang}`];
        const esContent = sec.content_es || {};
        let content;
        if (lang === 'es') {
            content = esContent || fallback;
        } else {
            const cleanedLangContent: Record<string, any> = {};
            if (langContent && typeof langContent === 'object') {
                for (const [key, val] of Object.entries(langContent)) {
                    if (val && typeof val === 'string') {
                        const esVal = esContent[key];
                        // If DB value is untranslated Spanish copied into content_en, skip it so it uses the dictionary translation
                        if (esVal && typeof esVal === 'string' && val.trim() === esVal.trim()) {
                            continue;
                        }
                    }
                    cleanedLangContent[key] = val;
                }
            }
            content = { ...fallback, ...cleanedLangContent };
        }
        return {
            ...fallback,
            ...content,
            is_visible: sec.is_visible !== undefined ? sec.is_visible : true,
            bg_image_url: sec.bg_image_url || fallback.bg_image_url
        };
    };

    const about = getSectionContent('about', {
        title: t.about_title,
        text: t.about_text,
        gallery: []
    });

    const philosophy = getSectionContent('philosophy', {
        phil_title: t.about_philosophy_title,
        phil_text: t.about_philosophy_text,
        loc_title: t.about_location_title,
        loc_text: t.about_location_text,
        safe_title: t.about_safety_title,
        safe_text: t.about_safety_text
    });

    return (
        <div className="animate-in fade-in duration-700 py-24 px-6 max-w-5xl mx-auto space-y-16">
            
            {/* Header Section */}
            <div className="space-y-6 text-center max-w-3xl mx-auto">
                <span className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-[0.3em] block">
                    {t.about_ecosystem || (lang === 'es' ? 'Nuestra Historia y Ecosistema' : 'Our Story and Ecosystem')}
                </span>
                <h1 className="text-4xl sm:text-6xl font-title uppercase tracking-tighter leading-none text-white">
                    {about.title}
                </h1>
                <div className="w-16 h-0.5 bg-[#00d4ff]/60 mx-auto mt-6" />
            </div>

            {/* Text / Philosophy Content Block */}
            <div className="max-w-3xl mx-auto space-y-8">
                <p className="text-lg sm:text-xl text-gray-300 leading-relaxed font-light whitespace-pre-wrap">
                    {about.text}
                </p>
                
                {/* philosophy subtitle/block if configured */}
                {(philosophy.phil_title || philosophy.phil_text) && (
                    <div className="pt-8 border-t border-rock/30 space-y-4">
                        <h2 className="text-xl font-title uppercase text-[#00d4ff] tracking-wider">
                            {philosophy.phil_title}
                        </h2>
                        <p className="text-sm sm:text-base text-gray-400 font-light leading-relaxed">
                            {philosophy.phil_text}
                        </p>
                    </div>
                )}

                {/* location and safety columns side-by-side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-rock/30">
                    <div>
                        <h3 className="text-xs font-bold text-[#00d4ff] uppercase tracking-widest mb-3">
                            {philosophy.loc_title}
                        </h3>
                        <p className="text-sm text-gray-400 font-light leading-relaxed">
                            {philosophy.loc_text}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-xs font-bold text-[#00d4ff] uppercase tracking-widest mb-3">
                            {philosophy.safe_title}
                        </h3>
                        <p className="text-sm text-gray-400 font-light leading-relaxed">
                            {philosophy.safe_text}
                        </p>
                    </div>
                </div>
            </div>

            {/* Carousel Section */}
            <div className="space-y-6">
                <div className="text-center">
                    <span className="text-[9px] text-gray-500 uppercase tracking-[0.2em] font-bold"> 
                        {lang === 'es' ? 'Galería de imágenes' : 'Image Gallery'} 
                    </span>
                </div>
                <ImageCarousel images={about.gallery && about.gallery.length > 0 ? about.gallery : DEFAULT_FALLBACK_IMAGES} />
            </div>
            
        </div>
    );
};

export default About;
